import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError, AxiosResponse, AxiosInstance } from 'axios';
import {
  MutateVariablePayload,
  MutationOptions,
  MutationResult,
  QueryResult,
  VariableDto,
} from '@thingslog/repositories';
import { QueryKeys } from '../enums/QueryKeys';
import { QueryOptions } from '@thingslog/repositories';
import { buildDashboardUrl } from './dashboardUrlUtils';

export function createDashboardVariableQueryClient(
  axios: AxiosInstance
): DashboardVariableQueryClient {
  return new DashboardVariableQueryClientImp(axios);
}

class DashboardVariableQueryClientImp {
  public constructor(private axios: AxiosInstance) {}

  public useDashboardVariables = (
    dashboardId: string,
    companyId?: string | number | null,
    options?: QueryOptions<VariableDto[]>
  ): QueryResult<VariableDto[]> => {
    return useQuery<VariableDto[], AxiosError>(
      [QueryKeys.GetVariables, dashboardId, companyId],
      () => this.getDashboardVariables(dashboardId, companyId),
      options
    );
  };

  public useDashboardVariable = (
    dashboardId: string,
    variableId: string,
    companyId?: string | number | null,
    options?: QueryOptions<VariableDto>
  ): QueryResult<VariableDto> => {
    return useQuery<VariableDto, AxiosError>(
      [QueryKeys.GetVariable, dashboardId, variableId, companyId],
      () => this.getDashboardVariable(dashboardId, variableId, companyId),
      options
    );
  };

  public useCreateDashboardVariable = (
    options?: MutationOptions<
      VariableDto,
      { dashboardId: string; body: MutateVariablePayload; companyId?: string | number | null }
    >
  ): MutationResult<
    VariableDto,
    { dashboardId: string; body: MutateVariablePayload; companyId?: string | number | null }
  > => {
    return useMutation<
      VariableDto,
      AxiosError,
      { dashboardId: string; body: MutateVariablePayload; companyId?: string | number | null }
    >(
      [QueryKeys.CreateVariable],
      ({
        dashboardId,
        body,
        companyId,
      }: {
        dashboardId: string;
        body: MutateVariablePayload;
        companyId?: string | number | null;
      }) => this.createDashboardVariable(dashboardId, body, companyId),
      options
    );
  };

  public useUpdateDashboardVariable = (
    options?: MutationOptions<
      VariableDto,
      {
        dashboardId: string;
        variableId: string;
        body: MutateVariablePayload;
        companyId?: string | number | null;
      }
    >
  ): MutationResult<
    VariableDto,
    {
      dashboardId: string;
      variableId: string;
      body: MutateVariablePayload;
      companyId?: string | number | null;
    }
  > => {
    return useMutation<
      VariableDto,
      AxiosError,
      {
        dashboardId: string;
        variableId: string;
        body: MutateVariablePayload;
        companyId?: string | number | null;
      }
    >(
      [QueryKeys.UpdateVariable],
      ({
        dashboardId,
        variableId,
        body,
        companyId,
      }: {
        dashboardId: string;
        variableId: string;
        body: MutateVariablePayload;
        companyId?: string | number | null;
      }) => this.updateDashboardVariable(dashboardId, variableId, body, companyId),
      options
    );
  };

  public useDeleteDashboardVariable = (
    options?: MutationOptions<
      boolean,
      { dashboardId: string; variableId: string; companyId?: string | number | null }
    >
  ): MutationResult<
    boolean,
    { dashboardId: string; variableId: string; companyId?: string | number | null }
  > => {
    return useMutation<
      boolean,
      AxiosError,
      { dashboardId: string; variableId: string; companyId?: string | number | null }
    >(
      [QueryKeys.DeleteVariable],
      ({
        dashboardId,
        variableId,
        companyId,
      }: {
        dashboardId: string;
        variableId: string;
        companyId?: string | number | null;
      }) => this.deleteDashboardVariable(dashboardId, variableId, companyId),
      options
    );
  };

  private getDashboardVariables = async (
    dashboardId: string,
    companyId?: string | number | null
  ): Promise<VariableDto[]> => {
    return await this.axios
      .get(buildDashboardUrl(`/dashboard/api/dashboards/${dashboardId}/variables`), {
        params: companyId != null ? { companyId } : {},
      })
      .then((response: AxiosResponse<VariableDto[]>) => response.data);
  };

  private getDashboardVariable = async (
    dashboardId: string,
    variableId: string,
    companyId?: string | number | null
  ): Promise<VariableDto> => {
    return await this.axios
      .get(buildDashboardUrl(`/dashboard/api/dashboards/${dashboardId}/variables/${variableId}`), {
        params: companyId != null ? { companyId } : {},
      })
      .then((response: AxiosResponse<VariableDto>) => response.data);
  };

  private createDashboardVariable = async (
    dashboardId: string,
    body: MutateVariablePayload,
    companyId?: string | number | null
  ): Promise<VariableDto> => {
    return await this.axios
      .post(buildDashboardUrl(`/dashboard/api/dashboards/${dashboardId}/variables`), body, {
        params: companyId != null ? { companyId } : {},
      })
      .then((response: AxiosResponse<VariableDto>) => response.data);
  };

  private updateDashboardVariable = async (
    dashboardId: string,
    variableId: string,
    body: MutateVariablePayload,
    companyId?: string | number | null
  ): Promise<VariableDto> => {
    return await this.axios
      .put(
        buildDashboardUrl(`/dashboard/api/dashboards/${dashboardId}/variables/${variableId}`),
        body,
        {
          params: companyId != null ? { companyId } : {},
        }
      )
      .then((response: AxiosResponse<VariableDto>) => response.data);
  };

  private deleteDashboardVariable = async (
    dashboardId: string,
    variableId: string,
    companyId?: string | number | null
  ): Promise<boolean> => {
    return await this.axios
      .delete(
        buildDashboardUrl(`/dashboard/api/dashboards/${dashboardId}/variables/${variableId}`),
        {
          params: companyId != null ? { companyId } : {},
        }
      )
      .then((response: AxiosResponse<boolean>) => response.data);
  };
}

export interface DashboardVariableQueryClient {
  useDashboardVariables: (
    dashboardId: string,
    companyId?: string | number | null,
    options?: QueryOptions<VariableDto[]>
  ) => QueryResult<VariableDto[]>;
  useDashboardVariable: (
    dashboardId: string,
    variableId: string,
    companyId?: string | number | null,
    options?: QueryOptions<VariableDto>
  ) => QueryResult<VariableDto>;
  useCreateDashboardVariable: (
    options?: MutationOptions<
      VariableDto,
      { dashboardId: string; body: MutateVariablePayload; companyId?: string | number | null }
    >
  ) => MutationResult<
    VariableDto,
    { dashboardId: string; body: MutateVariablePayload; companyId?: string | number | null }
  >;
  useUpdateDashboardVariable: (
    options?: MutationOptions<
      VariableDto,
      {
        dashboardId: string;
        variableId: string;
        body: MutateVariablePayload;
        companyId?: string | number | null;
      }
    >
  ) => MutationResult<
    VariableDto,
    {
      dashboardId: string;
      variableId: string;
      body: MutateVariablePayload;
      companyId?: string | number | null;
    }
  >;
  useDeleteDashboardVariable: (
    options?: MutationOptions<
      boolean,
      { dashboardId: string; variableId: string; companyId?: string | number | null }
    >
  ) => MutationResult<
    boolean,
    { dashboardId: string; variableId: string; companyId?: string | number | null }
  >;
}
